import React, { useState, useEffect } from 'react';
import { Modal, Checkbox } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box, Typography, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useMutation } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import { updateTicketData } from '../../../../api/ticket';

// ============================================
// Styled Components
// ============================================
const ModalContainer = styled(Box)({
  width: '520px',
  position: 'absolute',
  top: '50%',
  left: '50%',
  background: '#fff',
  borderRadius: '8px',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  height: '56px',
  background: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 32px',
});

const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#fff',
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: '#fff',
    fontSize: '24px',
  },
});

const ModalContent = styled(Box)({
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const FormLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#262626',
  width: '100px',
  flexShrink: 0,
});

const InputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
});

const StyledTextField = styled(TextField)({
  width: '80px',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    borderRadius: '6px',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
    '&:hover fieldset': {
      borderColor: '#d9d9d9',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0F7F85',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#262626',
    padding: '8px 12px',
    textAlign: 'center',
  },
});

const UnitText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#666',
  whiteSpace: 'nowrap',
});

const UnlimitedLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  cursor: 'pointer',
  '& .MuiCheckbox-root': {
    padding: '4px',
  },
});

const UnlimitedText = styled(Typography)({
  fontSize: '14px',
  color: '#666',
});

const ButtonWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '0 32px 32px',
});

// ============================================
// Component
// ============================================
const ReservationSettingModal = ({ modal, setModal, bigUser, ticket, user, getAllTickets, embedded = false, onDone }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const isOpen = embedded || modal.openReservationSetting;

  const [form, setForm] = useState({
    dailyUsageLimit: 0,
    dailyUnlimited: false,
    weeklyUsageLimit: 0,
    weeklyUnlimited: false,
    reservationCount: 0,
    reservationUnlimited: false,
    cancelLimit: 0,
    cancelLimitUnlimited: false,
  });

  // ticket 객체에서 직접 초기값 로드 (BE 응답에 이미 포함됨)
  // null = 무제한, 0 = 0회 제한
  useEffect(() => {
    if (isOpen && ticket) {
      setForm({
        dailyUsageLimit: ticket.dailyUsageLimit ?? 0,
        dailyUnlimited: ticket.dailyUsageLimit == null,
        weeklyUsageLimit: ticket.weeklyUsageLimit ?? 0,
        weeklyUnlimited: ticket.weeklyUsageLimit == null,
        reservationCount: ticket.reservationCount ?? 0,
        reservationUnlimited: ticket.reservationCount == null,
        cancelLimit: ticket.cancelLimit ?? 0,
        cancelLimitUnlimited: ticket.cancelLimit == null,
      });
    }
  }, [isOpen, ticket]);

  const onClose = () => {
    if (embedded) {
      onDone?.();
      return;
    }
    setModal(prev => ({ ...prev, openReservationSetting: false }));
  };

  const saveMutation = useMutation({
    mutationFn: data => updateTicketData(user?.memberId, ticket.memberCourseTicketId, data, bigUser.token),
    onSuccess: () => {
      toast.success(t('예약설정이 저장되었습니다'));
      getAllTickets();
      onClose();
    },
  });

  const handleNumberChange = (field, value) => {
    const cleaned = value.replace(/[^0-9]/g, '');
    const num = parseInt(cleaned, 10) || 0;
    setForm(prev => ({ ...prev, [field]: num }));
  };

  const handleCheckChange = (unlimitedKey, relatedFields) => {
    setForm(prev => {
      const newUnlimited = !prev[unlimitedKey];
      const updates = { [unlimitedKey]: newUnlimited };
      if (newUnlimited) {
        relatedFields.forEach(f => {
          updates[f] = 0;
        });
      }
      return { ...prev, ...updates };
    });
  };

  const handleSave = () => {
    // BE: null → 무제한, 0 → 0회 제한, 양수 → 해당 값
    const toNumber = v => (v === '' ? 0 : v);
    saveMutation.mutate({
      dailyUsageLimit: form.dailyUnlimited ? null : toNumber(form.dailyUsageLimit),
      weeklyUsageLimit: form.weeklyUnlimited ? null : toNumber(form.weeklyUsageLimit),
      reservationCount: form.reservationUnlimited ? null : toNumber(form.reservationCount),
      cancelLimit: form.cancelLimitUnlimited ? null : toNumber(form.cancelLimit),
    });
  };

  const body = (
    <>
    <ModalContent>
      {/* 일일 이용 제한 */}
      <FormRow>
        <FormLabel>{t('일일 제한')}</FormLabel>
        <InputGroup>
          {form.dailyUnlimited ? (
            <UnitText sx={{ width: '80px', textAlign: 'center', color: '#0F7F85', fontWeight: 600 }}>{t('무제한')}</UnitText>
          ) : (
            <>
              <StyledTextField
                type="text"
                value={form.dailyUsageLimit}
                onChange={e => handleNumberChange('dailyUsageLimit', e.target.value)}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
              <UnitText>{t('회')}</UnitText>
            </>
          )}
          <UnlimitedLabel onClick={() => handleCheckChange('dailyUnlimited', ['dailyUsageLimit'])}>
            <Checkbox size="small" checked={form.dailyUnlimited} />
            <UnlimitedText>{t('무제한')}</UnlimitedText>
          </UnlimitedLabel>
        </InputGroup>
      </FormRow>

      {/* 주간 이용 제한 */}
      <FormRow>
        <FormLabel>{t('주간 제한')}</FormLabel>
        <InputGroup>
          {form.weeklyUnlimited ? (
            <UnitText sx={{ width: '80px', textAlign: 'center', color: '#0F7F85', fontWeight: 600 }}>{t('무제한')}</UnitText>
          ) : (
            <>
              <StyledTextField
                type="text"
                value={form.weeklyUsageLimit}
                onChange={e => handleNumberChange('weeklyUsageLimit', e.target.value)}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
              <UnitText>{t('회')}</UnitText>
            </>
          )}
          <UnlimitedLabel onClick={() => handleCheckChange('weeklyUnlimited', ['weeklyUsageLimit'])}>
            <Checkbox size="small" checked={form.weeklyUnlimited} />
            <UnlimitedText>{t('무제한')}</UnlimitedText>
          </UnlimitedLabel>
        </InputGroup>
      </FormRow>

      {/* 최대 예약권 */}
      <FormRow>
        <FormLabel>{t('최대 예약권')}</FormLabel>
        <InputGroup>
          {form.reservationUnlimited ? (
            <UnitText sx={{ width: '80px', textAlign: 'center', color: '#0F7F85', fontWeight: 600 }}>{t('무제한')}</UnitText>
          ) : (
            <>
              <StyledTextField
                type="text"
                value={form.reservationCount}
                onChange={e => handleNumberChange('reservationCount', e.target.value)}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
              <UnitText>{t('회')}</UnitText>
            </>
          )}
          <UnlimitedLabel onClick={() => handleCheckChange('reservationUnlimited', ['reservationCount'])}>
            <Checkbox size="small" checked={form.reservationUnlimited} />
            <UnlimitedText>{t('무제한')}</UnlimitedText>
          </UnlimitedLabel>
        </InputGroup>
      </FormRow>

      {/* 예약 취소권 */}
      <FormRow>
        <FormLabel>{t('예약 취소권')}</FormLabel>
        <InputGroup>
          {form.cancelLimitUnlimited ? (
            <UnitText sx={{ width: '80px', textAlign: 'center', color: '#0F7F85', fontWeight: 600 }}>{t('무제한')}</UnitText>
          ) : (
            <>
              <StyledTextField
                type="text"
                value={form.cancelLimit}
                onChange={e => handleNumberChange('cancelLimit', e.target.value)}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
              <UnitText>{t('회')}</UnitText>
            </>
          )}
          <UnlimitedLabel onClick={() => handleCheckChange('cancelLimitUnlimited', ['cancelLimit'])}>
            <Checkbox size="small" checked={form.cancelLimitUnlimited} />
            <UnlimitedText>{t('무제한')}</UnlimitedText>
          </UnlimitedLabel>
        </InputGroup>
      </FormRow>
    </ModalContent>

    <ButtonWrapper>
      <CommonModalButton variant="cancel" onClick={onClose}>
        {t('취소')}
      </CommonModalButton>
      <CommonModalButton variant="submit" onClick={handleSave} disabled={saveMutation.isPending}>
        {t('저장')}
      </CommonModalButton>
    </ButtonWrapper>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Modal open={isOpen} onClose={onClose} disableEscapeKeyDown>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('예약설정')}</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        {body}
      </ModalContainer>
    </Modal>
  );
};

export default ReservationSettingModal;
